import { ref, get, set, push, update, remove, onValue, Unsubscribe } from './phpDataAdapter';
import { database } from '../config/backend';

export interface LandingText {
  heroTitle?: string;
  heroSubtitle?: string;
  heroBadge?: string;
  features?: Array<{
    id: string;
    title: string;
    description: string;
    icon: string;
  }>;
  [key: string]: any;
}

export interface LandingVideo {
  id: string;
  url: string;
  title: string;
  description: string;
  thumbnail?: string;
  type?: 'youtube' | 'vimeo' | 'direct';
  order?: number;
}

export interface LandingCard {
  id: string;
  title: string;
  description: string;
  icon: string;
  category?: string;
  order?: number;
}

export interface LandingArticle {
  id: string;
  title: string;
  content: string;
  author: string;
  date: string;
  pdfUrl?: string;
  thumbnail?: string;
  order?: number;
}

// Textos
export const getLandingTexts = async (): Promise<LandingText | null> => {
  try {
    const textsRef = ref(database, 'landingContent/texts');
    const snapshot = await get(textsRef);
    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error('Erro ao buscar textos da landing:', error);
    return null;
  }
};

export const updateLandingTexts = async (texts: Partial<LandingText>): Promise<boolean> => {
  try {
    const textsRef = ref(database, 'landingContent/texts');
    await update(textsRef, texts);
    return true;
  } catch (error) {
    console.error('Erro ao atualizar textos da landing:', error);
    return false;
  }
};

export const subscribeToLandingTexts = (
  callback: (texts: LandingText | null) => void
): Unsubscribe => {
  const textsRef = ref(database, 'landingContent/texts');
  return onValue(textsRef, (snapshot) => {
    callback(snapshot.exists() ? snapshot.val() : null);
  });
};

// Vídeos
export const getLandingVideos = async (): Promise<LandingVideo[]> => {
  try {
    const videosRef = ref(database, 'landingContent/videos');
    const snapshot = await get(videosRef);
    if (!snapshot.exists()) return [];
    
    const videos = snapshot.val();
    return Object.entries(videos)
      .map(([id, data]: [string, any]) => ({
        id,
        ...data
      }))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (error) {
    console.error('Erro ao buscar vídeos da landing:', error);
    return [];
  }
};

export const addLandingVideo = async (video: Omit<LandingVideo, 'id'>): Promise<string | null> => {
  try {
    const videosRef = ref(database, 'landingContent/videos');
    const newVideoRef = push(videosRef);
    await set(newVideoRef, {
      ...video,
      createdAt: new Date().toISOString()
    });
    return newVideoRef.key;
  } catch (error) {
    console.error('Erro ao adicionar vídeo:', error);
    return null;
  }
};

export const updateLandingVideo = async (videoId: string, updates: Partial<LandingVideo>): Promise<boolean> => {
  try {
    const videoRef = ref(database, `landingContent/videos/${videoId}`);
    await update(videoRef, updates);
    return true;
  } catch (error) {
    console.error('Erro ao atualizar vídeo:', error);
    return false;
  }
};

export const deleteLandingVideo = async (videoId: string): Promise<boolean> => {
  try {
    const videoRef = ref(database, `landingContent/videos/${videoId}`);
    await remove(videoRef);
    return true;
  } catch (error) {
    console.error('Erro ao deletar vídeo:', error);
    return false;
  }
};

// Cards
export const getLandingCards = async (): Promise<LandingCard[]> => {
  try {
    const cardsRef = ref(database, 'landingContent/cards');
    const snapshot = await get(cardsRef);
    if (!snapshot.exists()) return [];
    
    const cards = snapshot.val();
    return Object.entries(cards)
      .map(([id, data]: [string, any]) => ({
        id,
        ...data
      }))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (error) {
    console.error('Erro ao buscar cards da landing:', error);
    return [];
  }
};

export const addLandingCard = async (card: Omit<LandingCard, 'id'>): Promise<string | null> => {
  try {
    const cardsRef = ref(database, 'landingContent/cards');
    const newCardRef = push(cardsRef);
    await set(newCardRef, {
      ...card,
      createdAt: new Date().toISOString()
    });
    return newCardRef.key;
  } catch (error) {
    console.error('Erro ao adicionar card:', error);
    return null;
  }
};

export const updateLandingCard = async (cardId: string, updates: Partial<LandingCard>): Promise<boolean> => {
  try {
    const cardRef = ref(database, `landingContent/cards/${cardId}`);
    await update(cardRef, updates);
    return true;
  } catch (error) {
    console.error('Erro ao atualizar card:', error);
    return false;
  }
};

export const deleteLandingCard = async (cardId: string): Promise<boolean> => {
  try {
    const cardRef = ref(database, `landingContent/cards/${cardId}`);
    await remove(cardRef);
    return true;
  } catch (error) {
    console.error('Erro ao deletar card:', error);
    return false;
  }
};

// Artigos
export const getLandingArticles = async (): Promise<LandingArticle[]> => {
  try {
    const articlesRef = ref(database, 'landingContent/articles');
    const snapshot = await get(articlesRef);
    if (!snapshot.exists()) return [];
    
    const articles = snapshot.val();
    return Object.entries(articles)
      .map(([id, data]: [string, any]) => ({
        id,
        ...data
      }))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (error) {
    console.error('Erro ao buscar artigos da landing:', error);
    return [];
  }
};

export const addLandingArticle = async (article: Omit<LandingArticle, 'id'>): Promise<string | null> => {
  try {
    const articlesRef = ref(database, 'landingContent/articles');
    const newArticleRef = push(articlesRef);
    await set(newArticleRef, {
      ...article,
      createdAt: new Date().toISOString()
    });
    return newArticleRef.key;
  } catch (error) {
    console.error('Erro ao adicionar artigo:', error);
    return null;
  }
};

export const updateLandingArticle = async (articleId: string, updates: Partial<LandingArticle>): Promise<boolean> => {
  try {
    const articleRef = ref(database, `landingContent/articles/${articleId}`);
    await update(articleRef, updates);
    return true;
  } catch (error) {
    console.error('Erro ao atualizar artigo:', error);
    return false;
  }
};

export const deleteLandingArticle = async (articleId: string): Promise<boolean> => {
  try {
    const articleRef = ref(database, `landingContent/articles/${articleId}`);
    await remove(articleRef);
    return true;
  } catch (error) {
    console.error('Erro ao deletar artigo:', error);
    return false;
  }
};

// Função auxiliar para detectar tipo de vídeo
export const detectVideoType = (url: string): 'youtube' | 'vimeo' | 'direct' => {
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    return 'youtube';
  }
  if (url.includes('vimeo.com')) {
    return 'vimeo';
  }
  return 'direct';
};

// Função auxiliar para extrair ID do YouTube
export const extractYouTubeId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

// Função auxiliar para extrair ID do Vimeo
export const extractVimeoId = (url: string): string | null => {
  const regExp = /(?:vimeo)\.com.*(?:videos|video|channels|)\/([\d]+)/i;
  const match = url.match(regExp);
  return match ? match[1] : null;
};




